<template>
  <div class="agency-editor">
    <router-link :to="{ name: 'Agencies' }">← Partners</router-link>
    <h2>Partner #{{ id }}<span v-if="agency"> — {{ agency.name.en }}</span></h2>
    <div v-if="loading" class="card p-4">Loading partner…</div>
    <div v-else-if="loadError" class="card p-4">
      <p class="text-danger">{{ loadError }}</p>
      <button class="btn btn-primary" @click="load">Retry</button>
    </div>
    <template v-else-if="agency">
      <div class="agency-editor__status mb-3">
        {{ isSuspended ? 'Suspended' : 'Active' }} · {{ agency.is_blacklisted ? 'Blacklisted' : 'Not blacklisted' }}
      </div>
      <p v-if="managerError" class="text-warning">{{ managerError }}</p>
      <p v-if="!canManageAgencies" class="text-muted">Read-only. Partner management permission is required to make changes.</p>
      <el-tabs v-model="tab">
        <el-tab-pane label="Profile & contacts" name="core">
          <card title="Partner status">
            <p>{{ isSuspended ? 'This partner is suspended.' : 'This partner is active.' }}</p>
            <div v-if="canManageAgencies" class="agency-editor__activity-buttons">
              <button v-if="isSuspended" type="button" class="btn btn-success" :disabled="activitySaving || saving || dirty" @click="activate">{{ activitySaving ? 'Activating…' : 'Activate' }}</button>
              <button v-else type="button" class="btn btn-danger" :disabled="activitySaving || saving || dirty" @click="openDeactivate">Suspend</button>
            </div>
            <p v-if="canManageAgencies && dirty" class="text-muted">Save profile changes before changing partner status.</p>
            <p v-if="activityError" class="text-danger" role="alert">{{ activityError }}</p>
            <p v-if="lastDeactivation" class="text-muted">
              Last suspension: {{ lastDeactivation.reason_label || lastDeactivation.reason || '—' }}<span v-if="lastDeactivation.reason_defaulted"> (automatically assigned)</span>
              <span v-if="lastDeactivation.details"> · {{ lastDeactivation.details }}</span>
              <span v-if="lastDeactivation.actor && lastDeactivation.actor.full_name"> · by {{ lastDeactivation.actor.full_name }}</span>
              <span v-if="lastDeactivation.created_at"> · {{ new Date(lastDeactivation.created_at).toLocaleString() }}</span>
            </p>
          </card>
          <card title="Partner name">
            <div class="row">
              <div class="col-md-4" v-for="locale in ['en', 'ru', 'de']" :key="locale">
                <label class="d-block">Name ({{ locale.toUpperCase() }})</label>
                <el-input v-model="agency.name[locale]" :disabled="!canManageAgencies || saving" :aria-label="`Name (${locale.toUpperCase()})`" maxlength="255" />
              </div>
            </div>
          </card>
          <card v-for="section in coreSections" :key="section.title" :title="section.title">
            <div class="row">
              <div v-for="field in section.fields" :key="field[0]" class="col-md-6 mb-3">
                <label v-if="field[2] !== 'boolean'" class="d-block" :for="`core-${field[0]}`">{{ field[1] }}</label>
                <el-checkbox v-if="field[2] === 'boolean'" v-model="agency[field[0]]" :disabled="!canManageAgencies || saving" :id="`core-${field[0]}`">{{ field[1] }}</el-checkbox>
                <el-select v-else-if="field[2] === 'employee'" v-model="agency[field[0]]" filterable clearable :disabled="!canManageAgencies || saving || !!managerError" :id="`core-${field[0]}`" :aria-label="field[1]">
                  <el-option v-if="agency[field[0]] && !employeeChoices(field[0]).some(manager => manager.id === agency[field[0]])" :value="agency[field[0]]" :label="`#${agency[field[0]]}`" />
                  <el-option v-for="manager in employeeChoices(field[0])" :key="manager.id" :value="manager.id" :label="manager.name + (manager.fired ? ' (disabled)' : '')" :disabled="manager.fired" />
                </el-select>
                <el-select v-else-if="field[2] === 'tier'" v-model="agency.tier" :disabled="!canManageAgencies || saving" :id="`core-${field[0]}`" :aria-label="field[1]" placeholder="Select Partner Tier...">
                  <el-option value="" label="-- Not set --" />
                  <el-option v-if="agency.tier && !partnerTiers.includes(agency.tier)" :value="agency.tier" :label="agency.tier" disabled />
                  <el-option v-for="tier in partnerTiers" :key="tier" :value="tier" :label="tier" />
                </el-select>
                <el-input-number v-else-if="field[2] === 'number'" v-model="agency[field[0]]" :min="0" :precision="0" :disabled="!canManageAgencies || saving" :id="`core-${field[0]}`" :aria-label="field[1]" />
                <el-input v-else v-model="agency[field[0]]" :type="field[2] === 'textarea' ? 'textarea' : 'text'" :rows="3" :maxlength="field[2] === 'textarea' ? 65535 : 255" :disabled="!canManageAgencies || saving" :id="`core-${field[0]}`" />
                <p v-if="field[3]" class="text-muted mb-0">{{ field[3] }}</p>
              </div>
            </div>
          </card>
          <card v-for="contact in contactFields" :key="contact[0]" :title="contact[1]">
            <div class="row">
              <div class="col-md-6 mb-3" v-for="field in contact[2]" :key="field">
                <label :for="`${contact[0]}-${field}`">{{ field }}</label>
                <el-input v-model="settings[contact[0]][field]" maxlength="255" :disabled="!canManageAgencies || saving" :id="`${contact[0]}-${field}`" />
              </div>
            </div>
          </card>
        </el-tab-pane>
        <el-tab-pane v-for="section in parameterSections" :key="section.title" :label="section.title" :name="section.title">
          <card :title="section.title">
            <div class="row">
              <div v-for="field in section.fields" :key="field[0]" class="col-md-6 mb-3">
                <label v-if="field[2] !== 'boolean'" class="d-block" :for="`setting-${field[0]}`">{{ field[1] }}</label>
                <el-checkbox v-if="field[2] === 'boolean'" v-model="settings[field[0]]" :disabled="!canManageAgencies || saving" :id="`setting-${field[0]}`">{{ field[1] }}</el-checkbox>
                <el-input-number v-else-if="field[2] === 'number'" v-model="settings[field[0]]" :min="0" :precision="0" :disabled="!canManageAgencies || saving" :id="`setting-${field[0]}`" :aria-label="field[1]" />
                <el-input v-else v-model="settings[field[0]]" :type="field[2] === 'textarea' ? 'textarea' : field[2] === 'date' ? 'date' : 'text'" :disabled="!canManageAgencies || saving" :id="`setting-${field[0]}`" />
              </div>
            </div>
          </card>
        </el-tab-pane>
        <el-tab-pane label="Commission" name="commission">
          <card title="Commission"><router-link class="btn btn-primary" :to="{ name: 'AgencyCommission', params: { agencyId: id }, query: { name: agency.name.en } }">View commission terms and exception rules</router-link></card>
        </el-tab-pane>
        <el-tab-pane label="Schedule" name="schedule">
          <card title="Schedule">
            <p>Working hours in {{ agency.timezone }}.</p>
            <div class="mb-3">
              <label class="d-block">Availability</label>
              <el-radio-group v-model="agency.is_around_the_clock" :disabled="!canManageAgencies || saving" aria-label="Availability">
                <el-radio :label="true">Always open (24/7)</el-radio>
                <el-radio :label="false">Custom hours</el-radio>
              </el-radio-group>
              <p class="text-muted mb-0">Partners can also change their working hours in the partner account.</p>
            </div>
            <button class="btn btn-sm btn-default mb-3" :disabled="!canManageAgencies || saving || agency.is_around_the_clock" @click="initializeSchedule">{{ Object.keys(agency.schedule).length ? 'Reset weekly schedule' : 'Add weekly schedule' }}</button>
            <div class="row mb-3" v-for="day in Object.values(agency.schedule)" :key="day.date">
              <div class="col-md-3">{{ day.date }}</div>
              <div class="col-md-3"><el-input v-model="day.work_time_from" type="time" :disabled="!canManageAgencies || saving || agency.is_around_the_clock || day.is_weekend" :aria-label="`${day.date} from`" /></div>
              <div class="col-md-3"><el-input v-model="day.work_time_to" type="time" :disabled="!canManageAgencies || saving || agency.is_around_the_clock || day.is_weekend" :aria-label="`${day.date} to`" /></div>
              <div class="col-md-3"><el-checkbox v-model="day.is_weekend" :disabled="!canManageAgencies || saving || agency.is_around_the_clock">Closed</el-checkbox></div>
            </div>
          </card>
        </el-tab-pane>
        <el-tab-pane label="Users" name="users"><card title="Users"><agency-users v-if="tab === 'users'" :agency-id="id" /></card></el-tab-pane>
        <el-tab-pane label="Contracts" name="contracts"><card title="Contracts"><agency-contracts v-if="tab === 'contracts'" :agency-id="id" /></card></el-tab-pane>
        <el-tab-pane label="Payments" name="payments"><agency-payments v-if="tab === 'payments'" :key="id" :agency-id="id" /></el-tab-pane>
        <el-tab-pane label="Presentations" name="presentations">
          <card title="Presentations"><button class="btn btn-primary" :disabled="saving" @click="presentationsVisible = true">View presentations</button></card>
        </el-tab-pane>
        <el-tab-pane label="Clinics" name="clinics">
          <card title="Clinics">
            <p v-if="!agency.clinic_ids.length">No linked clinics.</p>
            <ul v-else><li v-for="clinicId in agency.clinic_ids" :key="clinicId"><router-link :to="{ name: 'Clinic', params: { clinicId } }">Clinic #{{ clinicId }}</router-link></li></ul>
          </card>
        </el-tab-pane>
        <div v-if="canManageAgencies && (!['users', 'payments'].includes(tab) || dirty || saving || saveError)" class="agency-editor__save row">
          <div class="col-12">
            <p v-if="saveError" class="text-danger" role="alert">{{ saveError }}</p>
            <button class="btn btn-primary" :disabled="!dirty || saving || activitySaving" @click="save">{{ saving ? 'Saving…' : 'Save changes' }}</button>
            <span class="ml-3">{{ dirty ? 'Unsaved changes' : 'All changes saved' }}</span>
          </div>
        </div>
      </el-tabs>
      <el-dialog title="Suspend partner" :visible.sync="deactivationVisible" width="480px" :close-on-click-modal="!activitySaving" :show-close="!activitySaving">
        <p>Select the reason for suspending this partner.</p>
        <p v-if="reasonsError" class="text-danger" role="alert">{{ reasonsError }}</p>
        <el-select v-model="deactivationReason" placeholder="Select a reason" class="agency-editor__reason" :loading="reasonsLoading" :disabled="reasonsLoading || activitySaving" aria-label="Suspension reason">
          <el-option v-for="reason in deactivationReasons" :key="reason.value" :label="reason.label" :value="reason.value" />
        </el-select>
        <el-input v-model="deactivationDetails" type="textarea" :rows="3" :maxlength="1000" show-word-limit placeholder="Details (optional)" :disabled="activitySaving" aria-label="Suspension details" class="mt-3" />
        <span slot="footer">
          <button type="button" class="btn btn-default" :disabled="activitySaving" @click="deactivationVisible = false">Cancel</button>
          <button type="button" class="btn btn-danger" :disabled="!deactivationReason || reasonsLoading || activitySaving || !canManageAgencies || dirty" @click="deactivate">{{ activitySaving ? 'Suspending…' : 'Suspend partner' }}</button>
        </span>
      </el-dialog>
      <agency-presentations-modal :visible.sync="presentationsVisible" :agency-id="Number(id)" />
    </template>
  </div>
</template>

<script>
import agencyAccess from '@/components/Agencies/agencyAccess';
import APIAgencies from '@/api/modules/agencies';
import AgencyUsers from '@/components/Agencies/AgencyUsers.vue';
import AgencyContracts from '@/components/Agencies/AgencyContracts.vue';
import AgencyPayments from '@/components/Agencies/AgencyPayments.vue';
import AgencyPresentationsModal from '@/components/Agencies/AgencyPresentationsModal.vue';
import { coreSections, parameterSections, contactFields, partnerTiers, scheduleScalarNames, copy, booleanValue, normalizeEditorAgency, requireAgencyResult, buildPatch } from '@/components/Agencies/editorFields';

export default {
  mixins: [agencyAccess],
  components: { AgencyPresentationsModal, AgencyContracts, AgencyUsers, AgencyPayments },
  props: { id: { type: [String, Number], required: true } },
  data: () => ({ agency: null, original: null, settings: {}, settingsBefore: {}, managers: [], employees: [], managerError: '', loadVersion: 0, activityVersion: 0, loading: true, saving: false, activitySaving: false, loadError: '', saveError: '', activityError: '', lastDeactivation: null, deactivationVisible: false, deactivationReasons: [], deactivationReason: '', deactivationDetails: '', reasonsLoading: false, reasonsError: '', tab: 'core', presentationsVisible: false, coreSections, parameterSections, contactFields, partnerTiers }),
  computed: {
    patch() { return this.agency ? buildPatch(this.original, this.agency, this.settingsBefore, this.settings) : {}; },
    dirty() { return Object.keys(this.patch).length > 0; },
    isSuspended() { return this.agency ? booleanValue(this.agency.is_hide) : false; },
  },
  watch: { id() { this.activityVersion++; this.deactivationVisible = false; this.activitySaving = false; this.activityError = ''; this.load(); } },
  created() { this.$store.dispatch('agencies/refreshPermissions'); this.load(); },
  mounted() { window.addEventListener('beforeunload', this.beforeUnload); },
  beforeDestroy() { this.loadVersion++; this.activityVersion++; window.removeEventListener('beforeunload', this.beforeUnload); },
  beforeRouteLeave(to, from, next) {
    if (this.saving || this.activitySaving) return next(false);
    if (!this.dirty) return next();
    this.$confirm('Discard unsaved partner changes?', 'Unsaved changes', { type: 'warning', confirmButtonText: 'Discard', cancelButtonText: 'Keep editing' }).then(() => next()).catch(() => next(false));
  },
  methods: {
    employeeChoices(field) { return field === 'financial_controller_id' ? this.employees : this.managers; },
    beforeUnload(event) { if (this.dirty) { event.preventDefault(); event.returnValue = ''; } },
    apply(data) {
      requireAgencyResult(data);
      const agency = normalizeEditorAgency(data.agency);
      agency.name = { en: '', ru: '', de: '', ...agency.name };
      agency.schedule = agency.schedule || {};
      agency.clinic_ids = (agency.clinic_ids || []).filter(id => id > 0);
      scheduleScalarNames.forEach(name => { agency[name] = booleanValue(agency[name]); });
      coreSections.forEach(section => section.fields.forEach(([name, label, type]) => {
        if (type === 'boolean') agency[name] = booleanValue(agency[name]);
        else if (type === 'employee') agency[name] = agency[name] || null;
        else if (type === 'number') agency[name] = Number(agency[name] || 0);
        else agency[name] = agency[name] || '';
      }));
      const raw = {};
      Object.values(data.agency_settings?.settings || {}).forEach(setting => { raw[setting.setting_name] = setting.setting_value; });
      const settings = {};
      parameterSections.forEach(section => section.fields.forEach(([name, label, type]) => {
        settings[name] = type === 'boolean' ? booleanValue(raw[name]) : type === 'number' ? Number(raw[name] || 0) : type === 'date' && raw[name] === '0000-00-00' ? '' : raw[name] || '';
      }));
      contactFields.forEach(([name, label, fields]) => {
        const contact = raw[name] ? JSON.parse(raw[name]) : {};
        settings[name] = {};
        fields.forEach(field => { settings[name][field] = contact[field] || ''; });
      });
      this.agency = agency;
      this.original = copy(agency);
      this.settings = settings;
      this.settingsBefore = copy(settings);
    },
    async load() {
      const version = ++this.loadVersion;
      const id = this.id;
      this.loading = true;
      this.loadError = '';
      try {
        const [agencyResult, managerResult, lastDeactivationResult] = await Promise.allSettled([APIAgencies.getAgency(id), APIAgencies.getManagers(), APIAgencies.getLastDeactivation(id)]);
        if (version !== this.loadVersion) return;
        if (agencyResult.status !== 'fulfilled') throw agencyResult.reason;
        this.apply(agencyResult.value.data);
        this.lastDeactivation = lastDeactivationResult.status === 'fulfilled' ? lastDeactivationResult.value : null;
        this.managerError = '';
        if (managerResult.status === 'fulfilled' && managerResult.value.data?.status !== false) {
          this.managers = managerResult.value.data?.managers || [];
          this.employees = managerResult.value.data?.employees || [];
        } else {
          this.managers = [];
          this.employees = [];
          this.managerError = 'Employee directory is unavailable. You can edit the other partner fields.';
        }
      } catch (error) { if (version === this.loadVersion) { this.agency = null; this.loadError = error.message || 'Unable to load partner'; } }
      finally { if (version === this.loadVersion) this.loading = false; }
    },
    initializeSchedule() {
      if (!this.canManageAgencies || this.saving || this.agency.is_around_the_clock) return;
      const schedule = {};
      ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].forEach((date, index) => {
        schedule[date] = { date, work_time_from: '09:00', work_time_to: '18:00', is_weekend: index > 4 };
      });
      this.agency.schedule = schedule;
    },
    async activate() {
      if (!this.canManageAgencies || !this.isSuspended || this.dirty || this.saving || this.activitySaving) return;
      try {
        await this.$confirm('Activate this partner?', 'Partner activation', { confirmButtonText: 'Activate', cancelButtonText: 'Cancel', type: 'warning' });
      } catch (e) { return; }
      await this.changeActivity(false);
    },
    async openDeactivate() {
      if (!this.canManageAgencies || this.isSuspended || this.dirty || this.saving || this.activitySaving) return;
      const id = this.id;
      const version = ++this.activityVersion;
      this.deactivationReason = '';
      this.deactivationDetails = '';
      this.activityError = '';
      this.reasonsError = '';
      this.deactivationVisible = true;
      this.reasonsLoading = true;
      try {
        const reasons = await APIAgencies.getDeactivationReasons();
        if (version === this.activityVersion && id === this.id) this.deactivationReasons = reasons;
      } catch (e) {
        if (version === this.activityVersion) { this.deactivationReasons = []; this.reasonsError = 'Suspension reasons unavailable. Try again.'; }
      } finally { if (version === this.activityVersion) this.reasonsLoading = false; }
    },
    async deactivate() {
      if (!this.deactivationVisible || !this.canManageAgencies || this.isSuspended || this.dirty || this.saving || this.activitySaving
        || !this.deactivationReasons.some(reason => reason.value === this.deactivationReason)) return;
      await this.changeActivity(true, this.deactivationReason, this.deactivationDetails);
    },
    async changeActivity(hidden, reason = null, details = '') {
      if (!this.canManageAgencies || this.isSuspended === hidden || this.dirty || this.saving || this.activitySaving) return;
      const id = this.id;
      const version = ++this.activityVersion;
      this.activitySaving = true;
      this.activityError = '';
      try {
        if (hidden) await APIAgencies.deactivateAgency(id, reason, details);
        else await APIAgencies.activateAgency(id);
        const { data } = await APIAgencies.getAgency(id);
        requireAgencyResult(data);
        if (booleanValue(data.agency.is_hide) !== hidden) throw new Error('Status was not verified. Refresh before retrying.');
        if (version !== this.activityVersion || id !== this.id) return;
        if (this.dirty) throw new Error('Partner status changed while you were editing. Save or discard your draft, then refresh.');
        this.apply(data);
        this.deactivationVisible = false;
        try {
          const last = await APIAgencies.getLastDeactivation(id);
          if (version === this.activityVersion && id === this.id) this.lastDeactivation = last;
        } catch (e) { if (version === this.activityVersion && id === this.id) this.lastDeactivation = null; }
        if (version !== this.activityVersion || id !== this.id) return;
        this.$notify({ type: 'success', title: hidden ? 'Partner suspended' : 'Partner activated' });
      } catch (e) {
        if (version === this.activityVersion) this.activityError = e.response?.data?.error?.code || e.message || 'Status was not verified. Refresh before retrying.';
      } finally { if (version === this.activityVersion) this.activitySaving = false; }
    },
    async save() {
      if (!this.canManageAgencies || !this.dirty || this.saving || this.activitySaving) return;
      this.saving = true;
      this.saveError = '';
      const patch = copy(this.patch);
      if (patch.schedule) patch.schedule = Object.values(patch.schedule);
      Object.keys(patch).forEach(name => {
        if (name.endsWith('_id') && patch[name] === '') patch[name] = null;
      });
      try {
        const { data } = await APIAgencies.updateAgency(this.id, patch);
        this.apply(data);
        this.$notify({ type: 'success', title: 'Partner changes saved' });
      } catch (error) {
        this.saveError = error.response?.data?.error?.code || error.message || 'Unable to save partner';
        this.$notify({ type: 'error', title: this.saveError });
      } finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.agency-editor .el-select { width: 100%; }
</style>
